import { createApp } from 'vue'
import jQuery from 'jquery'
import log4javascript from 'log4javascript'
import ElementPlus from 'element-plus'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'
import { createVfm } from 'vue-final-modal'
import 'vue-final-modal/style.css'
import VueTelInput from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import ToastPlugin from 'vue-toast-notification'
import 'vue-toast-notification/dist/theme-sugar.css'
import VueSweetalert2 from 'vue-sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import Vue3Progress from '@aacassandra/vue3-progressbar'
import './styles/global.css'
import vuetify from './plugins/vuetify'
import router from './router'
import store from './store'
import ApiManager from './api/ApiManager'
import Modal from './components/Modal.vue'
import LetterAvatar from './components/LetterAvatar.vue'
import { modalController } from './utils/modal'
import { appRoot } from './utils/appRoot'
import clickOutside from './components/clickOutside.js'
import selectOnFocus from './components/select-on-focus.js'
import AppCashbook from './AppCashbook.vue'

// =============================================================================
// Vue 3 migration boot — Phase 2 complete, Phase 4 in progress.
// Mount target is back to `AppCashbook.vue`; per-view runtime issues surface
// from here.
// =============================================================================

// jQuery is still relied on by ~100 call sites; keep the global available until
// Phase 5 cleanup audits those out.
window.$ = window.jQuery = jQuery

// Build stamp — baked in by Vite (see vite.config.js). Exposed on window and
// logged so you can confirm which build a deployment is actually serving.
window.__BUILD_TIME__ = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev'
console.info(`[sumtl] build ${window.__BUILD_TIME__}`)

// log4javascript stays — framework-agnostic.
const consoleAppender = new log4javascript.BrowserConsoleAppender()
const logLayout = new log4javascript.PatternLayout('%d{HH:mm:ss} %-5p [%c] - %m%n')
consoleAppender.setLayout(logLayout)

const rootLogger = log4javascript.getRootLogger()
rootLogger.addAppender(consoleAppender)

// Remote log shipping is optional — only wire up the AjaxAppender when a log URL
// was baked in at build time. `new AjaxAppender(undefined)` throws "URL must be
// specified in constructor", which would abort bootstrap and blank the page.
const logUrl = import.meta.env.VUE_APP_LOG_URL
if (logUrl) {
  const ajaxAppender = new log4javascript.AjaxAppender(logUrl)
  const jsonLayout = new log4javascript.JsonLayout()
  ajaxAppender.setLayout(jsonLayout)
  ajaxAppender.addHeader('Content-Type', 'application/json')
  ajaxAppender.setThreshold(log4javascript.Level.ERROR)
  ajaxAppender.setBatchSize(4)
  ajaxAppender.setSendAllOnUnload(true)
  ajaxAppender.setPostVarName(null)
  rootLogger.addAppender(ajaxAppender)
}
rootLogger.setLevel(
  import.meta.env.MODE === 'production' ? log4javascript.Level.INFO : log4javascript.Level.DEBUG,
)

rootLogger.info('Starting App (Vue 3)')

ApiManager.init()

const app = createApp(AppCashbook)
app.use(router)
app.use(store)
app.use(vuetify)
app.use(ElementPlus)
app.use(createVfm())
app.use(ToastPlugin)
app.use(VueSweetalert2)
app.use(Vue3Progress, {
  color: '#6554E0',
  failedColor: 'red',
  thickness: '5px',
  autoRevert: true,
  location: 'top',
  inverse: false,
  autoFinish: false,
})
// ATO STP phone (PAYEVNT16/PAYEVNTEMP95) limits canonical to 16 chars;
// maxLen=22 covers the formatted display with headroom.
app.use(VueTelInput, {
  validCharactersOnly: true,
  defaultCountry: 'au',
  preferredCountries: ['au'],
  disabledFetchingCountry: true,
  maxLen: 22,
  inputOptions: {
    showDialCode: false,
    tabindex: 0,
  },
})

// Register every Element Plus icon as a component so old `el-icon-*` font-class
// usages can be progressively swapped to `<el-icon><IconName /></el-icon>`
// without per-file imports. See @element-plus/icons-vue for the icon list.
for (const [name, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(name, component)
}

// Register <modal> globally so existing `<modal name="...">` usages keep
// resolving. See components/Modal.vue + utils/modal.js for the wrapper.
app.component('modal', Modal)
app.component('vue-letter-avatar', LetterAvatar)

app.directive('click-outside', clickOutside)
app.directive('select-on-focus', selectOnFocus)

// Globals previously hung off Vue.prototype.* — now on app.config.globalProperties.
app.config.globalProperties.$logger = rootLogger
app.config.globalProperties.$global = { path: '/' }
app.config.globalProperties.$modal = modalController
app.config.globalProperties.$appRoot = appRoot

app.config.errorHandler = (err, vm, info) => {
  const componentName = vm?.$options?.name || '<anonymous>'
  rootLogger.error(`Error in ${componentName} [info:${info}] ${err.message} -> ${err.stack}`)
}

// Eagerly load feature flags before first render so columns/fields gated on them
// (e.g. Qualifying Earnings) display correctly.
store.dispatch('fetchFeatureFlags');

app.mount('#app_cashbook')
